// `/debug` — the developer diagnostic surface, one TAB per question.
//
// The tabs are ordered the way a failure is chased: the DOOR (can this band connect a provider at all), the
// ENGINE (does the refresh reach each vendor and land anything), what Socials could DERIVE from what landed, the
// Songstats chain in detail, and Push (can a given person's phone be reached). Reading them in a different order
// means starting with the one vendor you already suspected. The active tab rides `?tab=` so a reload stays put.
//
// It is dev-only on BOTH sides, guarded by the same allowlist so the halves cannot disagree: every debug API
// refuses unless EnvName is a recognised non-production lane, and the SPA does not register this route outside
// one (IS_NON_PRODUCTION). In production `/debug` is Not Found, as if it had never been written.
//
// NOTE the folder is `features/diagnostics/`, not `features/debug/`, while the ROUTE is `/debug`. The repo's
// .gitignore carries the standard .NET `[Dd]ebug/` build-output pattern, which silently swallows any directory
// named `debug` anywhere in the tree — the page would work locally and simply not exist for anyone else. Do
// not "tidy" this back.
import { Page, Tabs } from "@cntrl/ui";
import { DEBUG_TABS, type DebugTab } from "./debugTabs.js";
import { ConnectPanel } from "./ConnectPanel.js";
import { SyncChainPanel } from "./SyncChainPanel.js";
import { SocialsPanel } from "./SocialsPanel.js";
import { SongstatsPanel } from "./SongstatsPanel.js";
import { PushPanel } from "./PushPanel.js";
import styles from "./DebugScreen.module.scss";

export interface DebugScreenProps {
  activeTab: DebugTab;
  onTab: (tab: DebugTab) => void;
}

export function DebugScreen({ activeTab, onTab }: DebugScreenProps) {
  return (
    <Page title="Debug">
      <Tabs tabs={DEBUG_TABS} active={activeTab} onChange={onTab} label="Debug sections" />
      <div className={styles.stack} role="tabpanel" aria-label={DEBUG_TABS.find((t) => t.key === activeTab)?.label}>
        {/* Only the active panel mounts, so a tab's reads (and the live probes some of them offer) run only
            when someone is looking at it. */}
        {activeTab === "connect" && <ConnectPanel />}
        {activeTab === "sync" && <SyncChainPanel />}
        {activeTab === "socials" && <SocialsPanel />}
        {activeTab === "songstats" && <SongstatsPanel />}
        {activeTab === "push" && <PushPanel />}
      </div>
    </Page>
  );
}
